import React, { useEffect, useState } from "react";
import { Typography, Grid, Box, Button } from "@mui/material";
import { Link } from "react-router-dom";

import "./TeamDraftPickHistoryGrid.css";

const INITIAL_YEARS_VISIBLE = 4;

function roundAccent(roundNum) {
  const r = Number(roundNum) || 1;
  const hue = (38 + (r - 1) * 34) % 360;
  return {
    "--tile-accent": `hsl(${hue}, 62%, 52%)`,
    "--round-border": `hsla(${hue}, 45%, 42%, 0.55)`,
    "--round-fg": `hsl(${hue}, 35%, 78%)`,
  };
}

function PickTile({ pick }) {
  const initials = `${(pick.first_name || "?")[0] || ""}${
    (pick.last_name || "?")[0] || ""
  }`.toUpperCase();

  return (
    <Link to={`/player/${pick.player_id}`} className="draft-pick-tile">
      <div className="draft-pick-tile-main">
        {pick.player_image_url ? (
          <img
            src={pick.player_image_url}
            alt=""
            className="draft-pick-tile-avatar"
            loading="lazy"
          />
        ) : (
          <div
            className="draft-pick-tile-avatar draft-pick-tile-avatar-placeholder"
            aria-hidden
          >
            {initials}
          </div>
        )}
        <div className="draft-pick-tile-text">
          <div className="draft-pick-tile-name">
            {pick.first_name} {pick.last_name}
          </div>
          <div className="draft-pick-tile-meta">
            #{pick.overall_pick != null ? pick.overall_pick : "—"} overall · Pick{" "}
            {pick.pick}
            {pick.position ? ` · ${pick.position}` : ""}
          </div>
        </div>
      </div>
    </Link>
  );
}

function TeamDraftPickHistoryGrid({ loading, data }) {
  const [showAllYears, setShowAllYears] = useState(false);
  const teamKey = data?.team_id ?? "";

  useEffect(() => {
    setShowAllYears(false);
  }, [teamKey]);

  if (loading) {
    return (
      <Grid item xs={12}>
        <Box className="container draft-pick-history">
          <div className="draft-pick-history-header">
            <Typography variant="body1" className="draft-pick-history-title">
              Draft pick grid
            </Typography>
          </div>
          <Typography
            variant="body2"
            sx={{ px: 2, py: 2, color: "var(--text-muted)" }}
          >
            Loading draft history…
          </Typography>
        </Box>
      </Grid>
    );
  }

  if (!data || !Array.isArray(data.years)) {
    return (
      <Grid item xs={12}>
        <Box className="container draft-pick-history">
          <div className="draft-pick-history-header">
            <Typography variant="body1" className="draft-pick-history-title">
              Draft pick grid
            </Typography>
          </div>
          <Typography
            variant="body2"
            sx={{ px: 2, py: 2, color: "var(--text-muted)" }}
          >
            Draft pick history could not be loaded.
          </Typography>
        </Box>
      </Grid>
    );
  }

  const { years } = data;
  const visibleYears = showAllYears
    ? years
    : years.slice(0, INITIAL_YEARS_VISIBLE);
  const hasMoreYears = years.length > INITIAL_YEARS_VISIBLE;

  if (years.length === 0) {
    return (
      <Grid item xs={12}>
        <Box className="container draft-pick-history">
          <div className="draft-pick-history-header">
            <Typography variant="body1" className="draft-pick-history-title">
              Draft pick grid
            </Typography>
          </div>
          <Typography
            variant="body2"
            sx={{ px: 2, py: 2, color: "var(--text-muted)" }}
          >
            No draft picks on file for this team yet.
          </Typography>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Box className="container draft-pick-history">
        <div className="draft-pick-history-header">
          <Typography variant="body1" className="draft-pick-history-title">
            Draft pick grid
          </Typography>
          <Typography variant="body2" className="draft-pick-history-sub">
            By year & round
          </Typography>
        </div>

        <div className="draft-pick-history-body">
          {visibleYears.map((yearBlock) => (
            <section
              className="draft-pick-year"
              key={yearBlock.draft_year}
              aria-label={`Draft ${yearBlock.draft_year}`}
            >
              <div className="draft-pick-year-gutter">
                <span className="draft-pick-year-dot" aria-hidden />
                <span className="draft-pick-year-chip">
                  {yearBlock.draft_year}
                </span>
              </div>

              <div className="draft-pick-rounds">
                {yearBlock.rounds.map((roundBlock) => (
                  <div
                    className="draft-pick-round-col"
                    key={`${yearBlock.draft_year}-r${roundBlock.round}`}
                    style={roundAccent(roundBlock.round)}
                  >
                    <div className="draft-pick-round-pill">
                      Round {roundBlock.round}
                    </div>
                    <div className="draft-pick-tiles">
                      {(roundBlock.picks || []).map((pick) => (
                        <PickTile
                          key={`${pick.player_id}-${pick.draft_year}-${pick.round}-${pick.pick}`}
                          pick={pick}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {hasMoreYears && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 1.25,
              px: 2,
              borderTop: "1px solid var(--border-subtle)",
            }}
          >
            {!showAllYears && (
              <Typography
                variant="caption"
                sx={{ color: "var(--text-muted)", letterSpacing: "0.02em" }}
              >
                Showing {visibleYears.length} of {years.length} draft years
              </Typography>
            )}
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAllYears((v) => !v)}
              sx={{
                color: "var(--brand-strong)",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.8rem",
              }}
            >
              {showAllYears
                ? `Show fewer (${INITIAL_YEARS_VISIBLE} years)`
                : `Show all ${years.length} years`}
            </Button>
          </Box>
        )}
      </Box>
    </Grid>
  );
}

export default TeamDraftPickHistoryGrid;
